import React, { useState, useEffect } from "react";
import { Github, ExternalLink } from "lucide-react";

const LANGUAGE_COLORS: Record<string, string> = {
  JavaScript: "#f7df1e",
  TypeScript: "#3178c6",
  Python: "#3572A5",
  Django: "#092E20",
  HTML: "#e34c26",
  CSS: "#264de4",
  Java: "#b07219",
  "C#": "#178600",
  "C++": "#f34b7d",
  Ruby: "#701516",
  Go: "#00ADD8",
  Rust: "#dea584",
  PHP: "#4F5D95",
  Swift: "#F05138",
  Kotlin: "#A97BFF",
  Shell: "#89e051",
  Vue: "#41b883",
  SCSS: "#c6538c",
};

interface Language {
  name: string;
  percent: number;
  color: string;
}

const projects = [
  {
    id: 1,
    title: "Elecciones Chile 2025",
    category: "React",
    year: "2025",
    description:
      "Simulador electoral chileno. Calcula resultados usando el método D'Hondt, visualiza el hemiciclo nacional y permite simulaciones de pactos.",
    image:
      "https://images.squarespace-cdn.com/content/v1/67891ce968700f53ef3c06a4/1758552734206-6T86HNANEESELBJ6P2WQ/Voto+Chile+2025.jpg?format=2500w",
    tech: ["React", "Python", "Flask", "Vite", "Pandas"],
    repo: "tomassolarr/dhondt-app",
    links: {
      code: "https://github.com/tomassolarr/dhondt-app",
      demo: "https://dhondt-tomassolar.netlify.app",
    },
  },
  {
    id: 2,
    title: "Psicología",
    category: "Vanilla",
    year: "2025",
    description: "Perfil profesional para psicóloga.",
    image:
      "https://i.ibb.co/hRGCrYQh/Captura-de-pantalla-2026-02-16-012642.png",
    tech: ["JavaScript", "CSS", "HTML"],
    repo: null,
    links: {
      code: "https://gladyspsicologa.cl/",
    },
  },
  {
    id: 3,
    title: "Cartelera Películas",
    category: "React",
    year: "2025",
    description:
      "Cartelera de películas consume TMDB. Explora, busca, ordena, guarda favoritos y ve detalles en modal.",
    image: "https://i.ibb.co/vCDvrn08/image.png",
    tech: ["React", "JavaScript", "MUI", "Vite"],
    repo: "tomassolarr/movie-app",
    links: {
      code: "https://github.com/tomassolarr/movie-app",
      demo: "https://cartelera-peliculass.netlify.app/",
    },
  },
  {
    id: 4,
    title: "Mi Música",
    category: "Django",
    year: "2025",
    description:
      "Aplicación web de gestión musical en Django. CRUD completo de artistas, álbumes y canciones con diseño moderno responsive.",
    image: "https://i.ibb.co/XfJv27zC/image.png",
    tech: ["Django", "Python", "Bootstrap", "HTML", "CSS"],
    repo: "tomassolarr/musicapp",
    links: {
      code: "https://github.com/tomassolarr/musicapp",
      demo: "https://musicapp-qwhq.onrender.com/",
    },
  },
  {
    id: 5,
    title: "Paraderos Chile",
    category: "React",
    year: "2025",
    description:
      "App para consultar paraderos de bus en Chile usando la API de Red Movilidad. Búsqueda por código, buses en tiempo real y favoritos.",
    image:
      "https://images2.mega.cl/meganoticias/2024/07/19/453153_3_669ad54009f5d.jpg",
    tech: ["React", "Vite", "React Router", "CSS"],
    repo: "tomassolarr/paradero-app",
    links: {
      code: "https://github.com/tomassolarr/paradero-app",
      demo: "https://revisa-tu-paradero.netlify.app/",
    },
  },
];

const fetchLanguages = async (repo: string): Promise<Language[]> => {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${repo}/languages`,
    );

    if (!response.ok) return [];

    const data = await response.json();

    if (Object.keys(data).length === 0) return [];

    const total = Object.values(data).reduce(
      (acc: number, val) => acc + (val as number),
      0,
    );

    return Object.entries(data).map(([name, bytes]) => ({
      name,
      percent: Math.round(((bytes as number) / total) * 100),
      color: LANGUAGE_COLORS[name] || "#6e7681",
    }));
  } catch (error) {
    return [];
  }
};

const Projects = () => {
  const [filter, setFilter] = useState("Todos");
  const [languages, setLanguages] = useState<Record<number, Language[]>>({});

  useEffect(() => {
    const loadLanguages = async () => {
      const langData: Record<number, Language[]> = {};
      for (const project of projects) {
        if (project.repo) {
          langData[project.id] = await fetchLanguages(project.repo);
        }
      }
      setLanguages(langData);
    };
    loadLanguages();
  }, []);

  const filteredProjects =
    filter === "Todos"
      ? projects
      : projects.filter((p) => p.category === filter);

  return (
    <section id="projects" className="mb-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-dark">Proyectos</h2>
      </div>

      <div className="flex gap-2 mb-6">
        {["Todos", "Vanilla", "React", "Django"].map((category) => (
          <button
            key={category}
            onClick={() => setFilter(category)}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded transition ${
              filter === category
                ? "bg-accent text-white"
                : "bg-bg2 text-dark hover:bg-accent hover:text-white"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-accent opacity-30"></div>

        <div className="space-y-8">
          {filteredProjects.map((project, index) => (
            <div key={project.id} className="relative pl-12">
              <div
                className={`absolute left-[0.375rem] top-2 w-3 h-3 rounded-full z-10 ${
                  index === 0
                    ? "bg-accent shadow-[0_0_10px_#CBA67B]"
                    : "bg-accent/50 border border-accent"
                }`}
              ></div>

              <div className="bg-bg2 border border-dark/10">
                <div className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-dark">
                        {project.title}
                      </h3>
                      <p className="text-xs text-dark opacity-60">
                        {project.category}
                      </p>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-accent bg-accent/10 px-2 py-1 rounded">
                      {project.year}
                    </span>
                  </div>

                  <div className="aspect-video w-full rounded-lg bg-dark/5 mb-4 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className={`w-full h-full object-cover border border-black/20 ${
                        project.title === "Elecciones Chile 2025"
                          ? "sepia-[0.3] brightness-90"
                          : ""
                      }`}
                    />
                  </div>

                  <p className="text-sm text-dark opacity-80 mb-4">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, i) => (
                      <span
                        key={i}
                        className="text-[11px] font-semibold text-accent px-2 py-0.5 rounded bg-accent/5 border border-accent/20"
                      >
                        #{tech}
                      </span>
                    ))}
                  </div>

                  {languages[project.id] &&
                    languages[project.id].length > 0 && (
                      <div className="mb-4">
                        <div className="flex h-2 rounded-full overflow-hidden bg-dark/10">
                          {languages[project.id].map((lang, i) => (
                            <div
                              key={i}
                              className="h-full"
                              style={{
                                width: `${lang.percent}%`,
                                backgroundColor: lang.color,
                              }}
                            />
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {languages[project.id].map((lang, i) => (
                            <span
                              key={i}
                              className="text-[10px] text-dark/60 flex items-center gap-1"
                            >
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: lang.color }}
                              ></span>
                              {lang.name} {lang.percent}%
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  <div className="flex gap-3">
                    {project.links.code && (
                      <a
                        href={project.links.code}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-dark text-white text-sm font-medium rounded hover:bg-accent transition"
                      >
                        {project.links.code.includes("github.com") ? (
                          <>
                            <Github size={16} />
                            Código
                          </>
                        ) : (
                          <>
                            <ExternalLink size={16} />
                            Proyecto
                          </>
                        )}
                      </a>
                    )}
                    {project.links.demo && (
                      <a
                        href={project.links.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-accent text-white text-sm font-medium rounded hover:opacity-80 transition"
                      >
                        <ExternalLink size={16} />
                        Proyecto
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
